import { useState, useMemo, useRef, useEffect } from 'react';
import Store from './Store';
import { getCombinedState } from './utils/getCombinedState';
import { shallowEqual } from './utils/shallowEqual';

export function useStore<State, T>(
    store: Store<State> | Store<unknown>[],
    selector?: (...args: State[] | unknown[]) => T,
): State | T {
    const stores = Array.isArray(store) ? store : [store];
    const [state, setState] = useState<State | T>(getCombinedState(stores, selector));
    const deps = useMemo(() => [...stores, selector], [store, selector]);
    const didMountRef = useRef<boolean>(false);

    useEffect(() => {
        const prevState = {
            value: state,
        };

        const subscriber = (): void => {
            const selectedState = getCombinedState(stores, selector);

            if (!shallowEqual(selectedState, prevState.value)) {
                prevState.value = selectedState;
                setState(selectedState);
            }
        };

        if (!didMountRef.current) {
            didMountRef.current = true;
        } else {
            subscriber();
        }

        stores.forEach((singleStore) => {
            singleStore.subscribe(subscriber);
        });

        return () => {
            stores.forEach((singleStore) => {
                singleStore.unsubscribe(subscriber);
            });
        };
    }, deps);

    return state;
}
